/**
 * MyRequestsSection Component
 * Feature 014: Service Provider Engagements - Care Seeker View
 *
 * Section showing all consultation requests submitted by the care seeker
 * Displays status badges (Pending/Accepted/Declined) and timestamps
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { FileText, AlertCircle, Loader2 } from 'lucide-react';
import { useMyConsultationRequests } from '../hooks/useConsultations';
import { MyRequestCard } from './MyRequestCard';

export function MyRequestsSection() {
  const navigate = useNavigate();
  const { data: requests, isLoading, error } = useMyConsultationRequests();

  // Sort requests by date (newest first) and group by status
  const sortedRequests = useMemo(() => {
    if (!requests) return [];

    return [...requests].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Newest first
    });
  }, [requests]);

  const handleViewConversation = (providerUserId: string) => {
    // Navigate to messages with provider pre-selected
    navigate(`/messages?userId=${providerUserId}`);
  };

  const handleViewProvider = (providerId: string) => {
    // Navigate to provider profile
    navigate(`/providers/${providerId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-[#026857]" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load your requests. Please try refreshing the page.
        </AlertDescription>
      </Alert>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No consultation requests yet</h3>
        <p className="text-gray-600 text-sm">
          Start by requesting a consultation from a provider's profile.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          My Consultation Requests ({requests.length})
        </h2>
      </div>

      <div className="space-y-4">
        {sortedRequests.map((request) => (
          <MyRequestCard
            key={request.id}
            request={request}
            onViewConversation={handleViewConversation}
            onViewProvider={handleViewProvider}
          />
        ))}
      </div>
    </div>
  );
}
